/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { useAppSelector } from '../hooks'
import { ITemplate } from '../interfaces/templates'
import Default from './Default'
import getAnalyticsItems from '../utils/getAnalyticsItems'
import { getAnalyticsCartPayload } from '../utils/getAnalyticsPayload'
import { useGetSummary } from '../hooks/summary'
import {
  useCheckoutRuleAutoSettle,
  useEndGuidedWalk,
  useSummaryCheckoutCta,
} from '../hooks/checkout'

/**
 * Summary screen.
 *
 * Thin like every template here. It fetches the summary, fires the GA4 / Meta
 * pageview, and — since COM-1048 — mounts the one-option auto-settle.
 *
 * The search-products modal also declares `type: 'summary'` in
 * `defaultConfig.ts`, which looks like a second mount of everything below and
 * is not one. `type` is read by `BaseContainer` alone, and `routes/index.tsx`
 * is its only caller; `ModalContainer` resolves a modal's own component tree
 * through `useLoadViews` and never consults `type`. So that member is inert.
 */
const Summary: FC<ITemplate> = ({ ...props }) => {
  const state = useAppSelector((stateRedux) => stateRedux)
  const { config } = state.defaultSlice
  const cart = state.cartSlice
  const getSummary = useGetSummary()

  /*
   * Mounted here as well as on the cart, because a shopper can reach the
   * summary without passing through it and the footer CTA here is the one that
   * fires `CREATE_ORDER`.
   *
   * The two mounts are **sequential, never simultaneous**: `<Routes>` renders
   * one matched route, each route element renders one `BaseContainer`, and
   * that switch picks one template. So whichever screen the shopper reaches
   * second finds the decision already made in `checkoutSlice.autoSettle`.
   *
   * ⚠️ That ordering is what makes it safe, not the store latch on its own.
   * Two instances alive at once would race: both would read `'resolving'` in
   * the same commit, and `advance` refuses only a **terminal** status, so both
   * would enter `'writing'` and call `select`. Mount this where one template
   * renders it, never from a component a page can hold alongside another.
   */
  useCheckoutRuleAutoSettle()

  /*
   * The footer CTA's caption. Reads the store and dispatches a token; starts
   * no fetch and owns no timer, so the one-live-instance warning above does
   * not reach it — but it is mounted here for the same reason, because this is
   * the screen whose footer binds `{{checkout.ctaLabel}}`.
   */
  useSummaryCheckoutCta()

  /* Arriving here ends any guided walk — see the hook for why the cart
   * mount beside it is not redundant. */
  useEndGuidedWalk()

  useEffect(() => {
    getSummary()
    const { pathname } = window.location
    const route = pathname.split('/').pop()
    if (route === 'summary') {
      const cartAnalyticsItems = getAnalyticsItems(cart)
      const analyticsPayload = getAnalyticsCartPayload(
        cart,
        cartAnalyticsItems,
        config,
      )
      ReactGA.event('view_summary', analyticsPayload)
      window.fbq('trackCustom', 'ViewCart', {
        ...analyticsPayload,
        currency: config?.options?.currencyPrefix,
      })
    }
  }, [])
  return <Default {...props} />
}

export default Summary
